import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../core/Icon';
import { IconButton } from '../core/IconButton';
import { Avatar } from '../core/Avatar';
import { AccountMenu } from './AccountMenu';
import type { AccountMenuProps } from './AccountMenu';
import { TopbarCompactaContexto } from './topbarCompacta';

export interface TopbarProps {
    breadcrumb?: ReactNode;
    search?: ReactNode;
    /** Abre a busca. Quando o campo sai por falta de espaço, fica um ícone que chama isto. */
    onSearch?: () => void;
    /** Ações do produto. Dentro delas, `useTopbarCompacta()` diz se é hora de virar ícone. */
    actions?: ReactNode;
    /** Rótulo do seletor de clínica. Abaixo de 880px de topbar, fica só o ícone. */
    clinic?: string;
    /** Logotipo da clínica, no lugar do ícone de prédio. Sem ele, fica o ícone. */
    clinicLogo?: string;
    /** Sem manipulador o seletor de clínica não é renderizado como botão. */
    onClinicClick?: () => void;
    /** Avatar simples. Prefira `account`, que rende o AccountMenu inteiro. */
    user?: string;
    /** Props repassadas ao menu de conta (nome, presença, tema, contas, versão, manipuladores). */
    account?: AccountMenuProps;
    /** Mostra a entrada do assistente de IA (o único gradiente do chrome). */
    onAssistant?: () => void;
    /** Nome acessível do orbe do assistente. Padrão: "Assistente VecchIA". */
    assistantLabel?: string;
    /** O painel do assistente está aberto: o orbe anuncia `aria-expanded`. */
    assistantExpanded?: boolean;
    /**
     * Abre a central de notificações. Sem manipulador o sininho não aparece: um botão
     * focável que não abre nada é um Tab a mais e uma promessa vazia.
     */
    onNotifications?: () => void;
    /** Não lidas: o número no sininho. Zero ou ausente, sem número. */
    notificationsCount?: number;
    /** Abre a ajuda. Sem manipulador o botão não aparece. */
    onHelp?: () => void;
    style?: CSSProperties;
}

export function Topbar({
    breadcrumb, search, onSearch, actions, clinic, clinicLogo, onClinicClick, user, account, onAssistant,
    assistantLabel = 'Assistente VecchIA', assistantExpanded, onNotifications, notificationsCount, onHelp, style,
}: TopbarProps) {
    const ref = useRef<HTMLElement | null>(null);
    const [largura, setLargura] = useState(1200);

    /* A topbar cede na ordem certa quando falta espaço.
       O caminho da página é o que diz ONDE a pessoa está: ele tem piso e trunca,
       enquanto os utilitários encolhem e, no aperto, saem. Antes o breadcrumb era
       o único item flexível e ia a zero — a busca com 260px fixos ganhava do
       caminho da página, que é prioridade invertida. */
    useEffect(() => {
        const alvo = ref.current;
        if (!alvo) return;
        const medir = () => setLargura(alvo.getBoundingClientRect().width);
        medir();
        /* O ResizeObserver já cobre o redimensionamento da janela — o listener
           de `resize` que existia ao lado dele media a mesma coisa duas vezes. */
        const ro = new ResizeObserver(medir);
        ro.observe(alvo);
        return () => ro.disconnect();
    }, []);

    const soIconeClinica = largura < 880;   // seletor de clínica perde o rótulo
    const semAjuda = largura < 760;         // ajuda sai: está no menu de conta
    const semBusca = largura < 620;         // o campo sai; fica o ícone, que abre a mesma busca
    /* Celular. Largura 0 é topbar ainda não medida (ou fora do layout): compactar
       aí esconderia rótulos de uma barra que ninguém está vendo encolhida. */
    const compacta = largura > 0 && largura < 640;
    /* O nome da clínica também está no menu da conta, com a troca. Só no telefone
       mais estreito (320 px de janela, 264 de topbar) ele cede, para o caminho da
       página caber; a 375 fica o ícone. */
    const semClinica = largura > 0 && largura < 300;

    return (
        <TopbarCompactaContexto.Provider value={compacta}>
            <header
                ref={ref}
                style={{
                    height: 56, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: compacta ? 6 : 12, padding: compacta ? '0 12px' : '0 20px',
                    background: 'var(--surface)', borderBottom: '1px solid var(--border-subtle)', minWidth: 0, ...style,
                }}
            >
                <div style={{ flex: '1 1 auto', minWidth: compacta ? 0 : 120, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 14 }}>{breadcrumb}</div>

                {search && !semBusca && (
                    <div style={{ flex: '0 1 auto', minWidth: 0, maxWidth: 260, overflow: 'hidden' }}>{search}</div>
                )}
                {onSearch && semBusca && (
                    <IconButton icon={<Icon name="search" size={17} />} label="Buscar ou comandar" onClick={onSearch} style={{ flex: '0 0 auto' }} />
                )}

                {clinic && onClinicClick && !semClinica && (
                    <button
                        type="button" title={clinic} onClick={onClinicClick}
                        /* Com o rótulo escondido sobra só o ícone de prédio, que não
                           nomeia nada: o nome acessível vem sempre do aria-label. */
                        aria-label={`Clínica: ${clinic}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: compacta ? '0 7px' : '0 10px', flex: '0 0 auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-control)', cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)', maxWidth: 200 }}
                    >
                        {clinicLogo
                            ? <img src={clinicLogo} alt="" style={{ width: 20, height: 20, objectFit: 'contain', flex: '0 0 auto', borderRadius: 4 }} />
                            : <Icon name="building-2" size={15} style={{ color: 'var(--neutral-500)', flex: '0 0 auto' }} />}
                        {!soIconeClinica && <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clinic}</span>}
                        {!compacta && <Icon name="chevron-down" size={13} style={{ color: 'var(--neutral-500)', flex: '0 0 auto' }} />}
                    </button>
                )}
                {/* Sem manipulador é rótulo, não botão: um botão focável que não abre
                    nada é um Tab a mais para quem usa teclado, e uma promessa vazia
                    para quem clica. Trocar de clínica fica no menu da conta. */}
                {clinic && !onClinicClick && !semClinica && (
                    <span
                        title={clinic} aria-label={`Clínica: ${clinic}`} role="img"
                        style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: compacta ? '0 6px' : '0 10px', flex: '0 0 auto', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)', maxWidth: 200 }}
                    >
                        {clinicLogo
                            ? <img src={clinicLogo} alt="" style={{ width: 20, height: 20, objectFit: 'contain', flex: '0 0 auto', borderRadius: 4 }} />
                            : <Icon name="building-2" size={15} style={{ color: 'var(--neutral-500)', flex: '0 0 auto' }} />}
                        {!soIconeClinica && <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clinic}</span>}
                    </span>
                )}
                {actions}
                {onNotifications && (
                    <span data-sininho="" style={{ position: 'relative', display: 'inline-flex', flex: '0 0 auto' }}>
                        <IconButton
                            icon={<Icon name="bell" size={17} />}
                            label={notificationsCount ? `Notificações, ${notificationsCount} não lidas` : 'Notificações'}
                            onClick={onNotifications}
                        />
                        {!!notificationsCount && (
                            /* Tinta forte: branco sobre o vermelho claro dava 3,8:1 em número pequeno. */
                            <span
                                aria-hidden="true"
                                style={{
                                    position: 'absolute', top: 1, right: 1, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 999,
                                    background: 'var(--danger-strong)', color: '#fff', fontFamily: 'var(--font-ui)', fontSize: 10.5, fontWeight: 700,
                                    lineHeight: '16px', textAlign: 'center', fontVariantNumeric: 'tabular-nums', pointerEvents: 'none',
                                }}
                            >
                                {notificationsCount > 99 ? '99+' : notificationsCount}
                            </span>
                        )}
                    </span>
                )}
                {onHelp && !semAjuda && <IconButton icon={<Icon name="circle-help" size={17} />} label="Ajuda" onClick={onHelp} />}
                {onAssistant && (
                    <button
                        type="button" onClick={onAssistant} title={assistantLabel} aria-label={assistantLabel}
                        aria-haspopup="dialog" aria-expanded={assistantExpanded}
                        style={{ width: 32, height: 32, flex: '0 0 auto', borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--ai-gradient)', cursor: 'pointer', boxShadow: 'var(--ai-glow)' }}
                    />
                )}
                {account ? <AccountMenu {...account} style={{ flex: '0 0 auto', ...account.style }} /> : user ? <Avatar name={user} size="sm" /> : null}
            </header>
        </TopbarCompactaContexto.Provider>
    );
}
